import axios from "axios";
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import ReactPaginate from "react-paginate";
import toast, { Toaster } from "react-hot-toast";
import XLSX from "xlsx-js-style";
import { saveAs } from "file-saver";
import { useMediaQuery } from "react-responsive";
import "./um.css";

const initialsOf = (name = "") =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase() || "?";

// Designation → themeable tint
const desigTint = (d = "") =>
  /graphic/i.test(d)
    ? { bg: "var(--color-violet-bg)", color: "var(--color-violet-text)" }
    : { bg: "var(--color-info-bg)", color: "var(--color-info-text)" };

const UserManagement = () => {
  const isMobile = useMediaQuery({ query: "(max-width: 768px)" });
  const Navigate = useNavigate();
  const location = useLocation();
  const [monthFilter, setMonthFilter] = useState("");
  let is_alert = false;
  const user = localStorage.getItem("user");
  const userData = JSON?.parse(user);
  const userType = userData?.user_type;

  const [selectedUser, setSelectedUser] = useState(null);
  const [dense, setDense] = useState(false);
  const [sortKey, setSortKey] = useState("");
  const [sortDir, setSortDir] = useState("asc");

  const handleRowClick = (id) => {
    const newId = selectedUser === id ? null : id;
    setSelectedUser(newId);
  };

  useEffect(() => {
    if (location.state !== null) {
      if (is_alert === false) {
        toast.success(location.state);
      }
      is_alert = true;
    }
    window.history.replaceState({}, location.state);
  }, []);

  const [data, setData] = useState([]);
  const [totalpage, setTotalPage] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  const handlePageClick = (e) => {
    setPage(e.selected + 1);
  };

  const UserManagementData = () => {
    const searchQuery = search ? `&search=${search}` : "";
    // Backend (subAdminModulePermissionsController.js) reads this as
    // `filter`, not `month` — this was sending a param name the backend
    // never looked at, so the dropdown silently did nothing.
    const monthQuery = monthFilter ? `&filter=${monthFilter}` : "";

    setLoading(true);
    axios
      .get(
        `${process.env.REACT_APP_URL}/admin/sub_admin_module_permissions?page=${page}${searchQuery}${monthQuery}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("Token")}`,
          },
        }
      )
      .then((res) => {
        const pagination = res?.data?.data?.pagination;

        setTotalPage(pagination?.totalPages || 1);

        const permissions = res?.data?.data?.permissions || [];

        const uniqueUserIds = [...new Set(permissions.map((p) => p.user_id))];

        const grouped = permissions.reduce((acc, item) => {
          const uid = item.user_id;

          if (!acc[uid]) {
            acc[uid] = {
              user_id: uid,
              name: item?.subAdmin?.name || item?.name || "",
              designation:
                item?.designation === "1"
                  ? "Graphic Designer"
                  : item?.designation === "2"
                  ? "Sales Executive"
                  : item?.designation || "",
              email: item?.subAdmin?.email || "",
              phone: item?.subAdmin?.phone || "",
              role: "SubAdmin",
              created_at: item?.created_at,
              permissions: [],
            };
          }

          acc[uid].permissions.push({
            module_id: item.module_id,
            module_name: item.module?.name,
            sub_module_id: item.sub_module_id,
            sub_module_name: item.subModule?.name,
          });

          return acc;
        }, {});

        let groupedArray = Object.values(grouped);

        // ✅ Sort or reverse for latest first
        // Option 1: Reverse if backend sends latest last
        groupedArray = groupedArray.reverse();

        // Option 2 (better): Sort explicitly by created_at descending
        groupedArray.sort(
          (a, b) => new Date(b.created_at) - new Date(a.created_at)
        );

        setData(groupedArray);
      })
      .catch((err) => {
        console.error("API Error:", err);
        if (err.response?.data?.message === "Unauthorized!") {
          Navigate("/");
        } else {
          toast.error("Failed to fetch data");
        }
      })
      .finally(() => setLoading(false));
  };

  const exportToExcel = async () => {
    const params = new URLSearchParams();

    if (search) params.append("search", search);
    if (monthFilter) params.append("filter", monthFilter);

    params.append("page", 1);
    params.append("limit", 10000);

    try {
      const response = await axios.get(
        `${
          process.env.REACT_APP_URL
        }/admin/sub_admin_module_permissions?${params.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("Token")}`,
          },
        }
      );

      const permissions = response?.data?.data?.permissions || [];

      if (permissions.length === 0) {
        alert("No data to export.");
        return;
      }

      // Group the data by user_id (same logic as UserManagementData)
      const grouped = permissions.reduce((acc, item) => {
        const uid = item.user_id;

        if (!acc[uid]) {
          acc[uid] = {
            user_id: uid,
            name: item?.subAdmin?.name || item?.name || "",
            designation:
              item?.designation === "1"
                ? "Graphic Designer"
                : item?.designation === "2"
                ? "Sales Executive"
                : item?.designation || "",
            email: item?.subAdmin?.email || "",
            phone: item?.subAdmin?.phone || "",
            role: "SubAdmin",
            created_at: item?.created_at,
            permissions: [],
          };
        }

        acc[uid].permissions.push({
          module_id: item.module_id,
          module_name: item.module?.name,
          sub_module_id: item.sub_module_id,
          sub_module_name: item.subModule?.name,
        });

        return acc;
      }, {});

      const exportData = Object.values(grouped);

      // Format data for export
      const formattedData = exportData.map((user, index) => {
        // Combine permissions into a readable string
        const permissionsText = user.permissions
          .map(
            (p) =>
              `${p.module_name}${
                p.sub_module_name ? ` - ${p.sub_module_name}` : ""
              }`
          )
          .join(", ");

        return {
          "Sr. No.": index + 1,
          Name: user?.name || "",
          Designation: user?.designation || "",
          Email: user?.email || "",
          "Mobile Number": user?.phone || "N/A",
          Role: user?.role || "",
          Permissions: permissionsText,
          "Created Date": new Date(user?.created_at).toLocaleDateString(
            "en-IN",
            {
              day: "2-digit",
              month: "short",
              year: "numeric",
            }
          ),
          "Created Time": new Date(user?.created_at).toLocaleTimeString(
            "en-IN",
            {
              hour: "2-digit",
              minute: "2-digit",
              hour12: false,
            }
          ),
        };
      });

      // Extract headers
      const header = Object.keys(formattedData[0]);

      // Create worksheet without headers first
      const worksheet = XLSX.utils.json_to_sheet(formattedData, {
        skipHeader: true,
        origin: "A2",
      });

      // Add custom header row
      XLSX.utils.sheet_add_aoa(worksheet, [header], { origin: "A1" });

      // Set column widths
      worksheet["!cols"] = header.map((h) => ({
        wch: Math.max(h.length + 2, 15),
      }));

      // === Styling ===
      // Header styling (row 1)
      header.forEach((col, i) => {
        const cell = worksheet[XLSX.utils.encode_cell({ r: 0, c: i })];
        if (cell) {
          cell.s = {
            fill: { fgColor: { rgb: "ADD8E6" } }, // Light blue background
            font: { bold: true, color: { rgb: "000000" } }, // Black bold text
            alignment: { horizontal: "center", vertical: "center" },
          };
        }
      });

      // Alternate row striping
      formattedData.forEach((_, rowIndex) => {
        if (rowIndex % 2 === 1) {
          header.forEach((_, colIndex) => {
            const cell =
              worksheet[
                XLSX.utils.encode_cell({ r: rowIndex + 1, c: colIndex })
              ];
            if (cell) {
              cell.s = {
                fill: { fgColor: { rgb: "F2F2F2" } }, // Light gray background
                alignment: { horizontal: "left", vertical: "center" },
              };
            }
          });
        }
      });

      // Create workbook
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Users Management");

      // Write Excel file
      const excelBuffer = XLSX.write(workbook, {
        bookType: "xlsx",
        type: "array",
        cellStyles: true,
      });

      const fileData = new Blob([excelBuffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
      });

      saveAs(fileData, "users-management.xlsx");
    } catch (err) {
      console.error("Export failed", err);
      alert("Failed to export data.");
    }
  };

  useEffect(() => {
    UserManagementData();
  }, [page, search, monthFilter]);

  const formatDate = (d) =>
    d
      ? new Date(d).toLocaleString("en-GB", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
          hour12: false,
        })
      : "N/A";

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const displayData = useMemo(() => {
    const rows = [...(data || [])];
    if (sortKey) {
      rows.sort((a, b) => {
        let av;
        let bv;
        if (sortKey === "created_at") {
          av = new Date(a.created_at).getTime() || 0;
          bv = new Date(b.created_at).getTime() || 0;
        } else {
          av = (a[sortKey] || "").toString().toLowerCase();
          bv = (b[sortKey] || "").toString().toLowerCase();
        }
        if (av < bv) return sortDir === "asc" ? -1 : 1;
        if (av > bv) return sortDir === "asc" ? 1 : -1;
        return 0;
      });
    }
    return rows;
  }, [data, sortKey, sortDir]);

  const SortIcon = ({ active }) => (
    <svg
      width="11"
      height="11"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{
        opacity: active ? 1 : 0.45,
        transform: active && sortDir === "desc" ? "rotate(180deg)" : "none",
        transition: "transform .15s ease",
      }}
    >
      <polyline points="18 15 12 9 6 15" />
    </svg>
  );

  const RowActions = ({ item }) => (
    <div className="um-actions">
      <button
        type="button"
        className="um-act"
        title="View"
        onClick={(e) => {
          e.stopPropagation();
          Navigate("/admin/usermanagement/show", { state: item });
        }}
      >
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
          <circle cx="12" cy="12" r="3" />
        </svg>
      </button>
      <button
        type="button"
        className="um-act um-act--primary"
        title="Edit"
        onClick={(e) => {
          e.stopPropagation();
          Navigate("/admin/usermanagement/edit", { state: item });
        }}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
        </svg>
      </button>
    </div>
  );

  return (
    <div className="um">
      <Toaster position="top-center" reverseOrder={false} />

      {/* Page header */}
      <div className="um-head">
        <div>
          <h1 className="um-title">User Management</h1>
          <p className="um-sub">
            Manage sub-admins and their module access across the platform.
          </p>
        </div>
        <div className="um-head__actions">
          {userType === "admin" && (
            <button type="button" className="um-btn" onClick={exportToExcel}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
              Export
            </button>
          )}
          <button
            type="button"
            className="um-btn um-btn--primary"
            onClick={() => Navigate("/admin/usermanagement/add")}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            Add Sub Admin
          </button>
        </div>
      </div>

      {/* Table card */}
      <div className="um-card">
        {/* toolbar */}
        <div className="um-toolbar">
          <div className="um-search">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--color-text-tertiary)" strokeWidth="2" strokeLinecap="round">
              <circle cx="11" cy="11" r="7" />
              <path d="m21 21-4.3-4.3" />
            </svg>
            <input
              type="search"
              placeholder="Search by name, email, mobile…"
              onKeyDown={(e) => {
                if (e.key === "Enter" && search !== "") UserManagementData();
              }}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <div className="um-select">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="4" width="18" height="18" rx="2" />
              <line x1="16" y1="2" x2="16" y2="6" />
              <line x1="8" y1="2" x2="8" y2="6" />
              <line x1="3" y1="10" x2="21" y2="10" />
            </svg>
            <select
              value={monthFilter}
              onChange={(e) => {
                setMonthFilter(e.target.value);
                UserManagementData();
              }}
            >
              <option value="">Select Month</option>
              <option value="All">All</option>
              <option value="1">Last 1 Month</option>
              <option value="3">Last 3 Months</option>
              <option value="6">Last 6 Months</option>
            </select>
          </div>

          <button
            type="button"
            className="um-tool"
            style={{ marginLeft: "auto" }}
            onClick={() => setDense((d) => !d)}
            title="Toggle density"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="3" y1="6" x2="21" y2="6" />
              <line x1="3" y1="12" x2="21" y2="12" />
              <line x1="3" y1="18" x2="21" y2="18" />
            </svg>
            {dense ? "Comfortable" : "Density"}
          </button>
        </div>

        {/* desktop grid table */}
        {!isMobile ? (
          <div className="um-tablewrap">
            <div className="um-table" style={{ "--um-row-pad": dense ? "8px" : "13px" }}>
              <div className="um-thead">
                <span className="um-th">Sr.</span>
                <span className="um-th um-th--sort" onClick={() => toggleSort("name")}>
                  Name <SortIcon active={sortKey === "name"} />
                </span>
                <span className="um-th um-th--sort" onClick={() => toggleSort("designation")}>
                  Designation <SortIcon active={sortKey === "designation"} />
                </span>
                <span className="um-th">Email</span>
                <span className="um-th">Mobile Number</span>
                <span className="um-th">Role</span>
                <span className="um-th um-th--sort" onClick={() => toggleSort("created_at")}>
                  Date &amp; Time <SortIcon active={sortKey === "created_at"} />
                </span>
                <span className="um-th" style={{ textAlign: "right", justifyContent: "flex-end" }}>
                  Action
                </span>
              </div>

              {loading &&
                [...Array(6)].map((_, i) => (
                  <div className="um-row" key={`sk-${i}`}>
                    <span className="um-skel" style={{ width: 18, height: 12 }} />
                    <div className="um-id">
                      <span className="um-skel" style={{ width: 32, height: 32, borderRadius: 9 }} />
                      <span className="um-skel" style={{ width: 90, height: 12 }} />
                    </div>
                    <span className="um-skel" style={{ width: 80, height: 18, borderRadius: 6 }} />
                    <span className="um-skel" style={{ width: 120, height: 12 }} />
                    <span className="um-skel" style={{ width: 80, height: 12 }} />
                    <span className="um-skel" style={{ width: 60, height: 18, borderRadius: 6 }} />
                    <span className="um-skel" style={{ width: 110, height: 12 }} />
                    <span className="um-skel" style={{ width: 64, height: 30, borderRadius: 8, marginLeft: "auto" }} />
                  </div>
                ))}

              {!loading &&
                displayData.map((item, index) => {
                  const dt = desigTint(item.designation);
                  return (
                    <div
                      className={`um-row ${
                        selectedUser === String(item.user_id) ? "is-selected" : ""
                      }`}
                      key={item.user_id}
                      onClick={() => handleRowClick(item.user_id)}
                    >
                      <span className="um-mono" style={{ color: "var(--color-text-tertiary)", fontWeight: 700 }}>
                        {(page - 1) * 10 + index + 1}
                      </span>
                      <div className="um-id">
                        <span
                          className="um-avatar"
                          style={{ background: dt.bg, color: dt.color }}
                        >
                          {initialsOf(item.name)}
                        </span>
                        <span className="um-name">{item.name || "—"}</span>
                      </div>
                      <span>
                        {item.designation ? (
                          <span className="um-pill" style={{ background: dt.bg, color: dt.color }}>
                            {item.designation}
                          </span>
                        ) : (
                          <span className="um-muted">—</span>
                        )}
                      </span>
                      <span className="um-muted um-ellipsis">{item.email || "—"}</span>
                      <span className="um-mono">{item.phone || "N/A"}</span>
                      <span>
                        <span
                          className="um-pill"
                          style={{ background: "var(--color-violet-bg)", color: "var(--color-violet-text)" }}
                        >
                          {item.role}
                        </span>
                      </span>
                      <span className="um-mono um-muted">{formatDate(item.created_at)}</span>
                      <RowActions item={item} />
                    </div>
                  );
                })}

              {!loading && displayData.length === 0 && (
                <div className="um-empty">
                  <div className="um-empty__icon">
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                      <circle cx="9" cy="7" r="4" />
                      <path d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
                    </svg>
                  </div>
                  <p className="um-empty__title">No users found</p>
                  <p className="um-empty__text">Try adjusting your search or filters.</p>
                </div>
              )}
            </div>
          </div>
        ) : (
          // Mobile cards
          <div className="um-mobile">
            {!loading && data?.length !== 0 ? (
              data.map((item, index) => {
                const dt = desigTint(item.designation);
                return (
                  <div className="um-mcard" key={item.user_id}>
                    <div className="um-mcard__top">
                      <span className="um-avatar" style={{ background: dt.bg, color: dt.color }}>
                        {initialsOf(item.name)}
                      </span>
                      <div style={{ minWidth: 0 }}>
                        <div className="um-name">{item.name || "—"}</div>
                        <div className="um-muted um-ellipsis">{item.email || "—"}</div>
                      </div>
                      <span
                        className="um-pill"
                        style={{ marginLeft: "auto", background: "var(--color-violet-bg)", color: "var(--color-violet-text)" }}
                      >
                        {item.role}
                      </span>
                    </div>
                    <div className="um-mrow">
                      <span className="um-muted">Sr. No.</span>
                      <span className="um-mono">{(page - 1) * 10 + index + 1}</span>
                    </div>
                    <div className="um-mrow">
                      <span className="um-muted">Designation</span>
                      <span>{item.designation || "—"}</span>
                    </div>
                    <div className="um-mrow">
                      <span className="um-muted">Mobile</span>
                      <span className="um-mono">{item.phone || "N/A"}</span>
                    </div>
                    <div className="um-mrow">
                      <span className="um-muted">Date</span>
                      <span className="um-mono um-muted">{formatDate(item.created_at)}</span>
                    </div>
                    <div className="um-mactions">
                      <RowActions item={item} />
                    </div>
                  </div>
                );
              })
            ) : (
              !loading && (
                <div className="um-empty">
                  <p className="um-empty__title">No Record Found</p>
                </div>
              )
            )}
          </div>
        )}

        {/* pagination */}
        <div className="um-foot">
          <span className="um-foot__info">
            Showing <strong>{displayData.length}</strong> sub-admins
          </span>
          {data?.length !== 0 && (
            <ReactPaginate
              previousLabel={
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="15 18 9 12 15 6" />
                </svg>
              }
              nextLabel={
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="9 18 15 12 9 6" />
                </svg>
              }
              breakLabel={"..."}
              pageCount={totalpage}
              marginPagesDisplayed={2}
              pageRangeDisplayed={2}
              onPageChange={handlePageClick}
              containerClassName={"pagination justify-content-end m-0"}
              pageClassName={"page-item"}
              pageLinkClassName={"page-link"}
              previousClassName={"page-item"}
              previousLinkClassName={"page-link"}
              nextClassName={"page-item"}
              nextLinkClassName={"page-link"}
              breakClassName={"page-item"}
              breakLinkClassName={"page-link"}
              activeClassName={"active"}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default UserManagement;
